import React, { useState, useEffect, useRef, useMemo } from 'react';

interface Account {
  id: number;
  name: string;
}

interface Props {
  accounts: Account[];
  selectedValues: string[];
  onChange: (selectedValues: string[]) => void;
  placeholder?: string;
  className?: string;
}

export const MultiSelectAccountDropdown: React.FC<Props> = ({
  accounts,
  selectedValues,
  onChange,
  placeholder = "All accounts",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [openUpward, setOpenUpward] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<(HTMLDivElement | null)[]>([]);

  // Filter accounts based on search term
  const filteredAccounts = useMemo(() => {
    if (!searchTerm) return accounts;
    
    const searchLower = searchTerm.toLowerCase();
    return accounts.filter(account =>
      account.name.toLowerCase().includes(searchLower)
    );
  }, [accounts, searchTerm]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check dropdown position
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      if (spaceBelow < 200 && spaceAbove > spaceBelow) {
        setOpenUpward(true);
      } else {
        setOpenUpward(false);
      }
    }
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggleAccount = (accountId: string) => {
    if (selectedValues.includes(accountId)) {
      onChange(selectedValues.filter(v => v !== accountId));
    } else {
      onChange([...selectedValues, accountId]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        break;
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredAccounts.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredAccounts.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredAccounts.length) {
          handleToggleAccount(filteredAccounts[highlightedIndex].id.toString());
        }
        break;
    }
  };

  // Get display text for the input
  const getDisplayText = () => {
    if (selectedValues.length === 0) {
      return '';
    }

    const selectedAccounts = accounts.filter(account => 
      selectedValues.includes(account.id.toString())
    );
    
    if (selectedValues.length <= 2) {
      return selectedAccounts.map(account => account.name).join(', ');
    }

    const firstTwo = selectedAccounts.slice(0, 2).map(account => account.name);
    const remaining = selectedValues.length - 2;
    return `${firstTwo.join(', ')} +${remaining} more`;
  };

  const getPlaceholder = () => {
    return selectedValues.length > 0 ? '' : placeholder;
  };

  return (
    <div className={`multi-select-filter ${className}`} ref={dropdownRef}>
      <div className={`filter-input-container ${isOpen ? 'open' : ''}`}>
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : getDisplayText()}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setHighlightedIndex(0);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => {
            setIsOpen(true);
            setSearchTerm('');
          }}
          onKeyDown={handleKeyDown}
          placeholder={getPlaceholder()}
          className="filter-input"
          autoComplete="off"
        />
        <div 
          className="filter-arrow"
          onClick={() => {
            setIsOpen(!isOpen);
            if (!isOpen) {
              inputRef.current?.focus();
            }
          }}
        >
          ▼
        </div>
      </div>

      {isOpen && (
        <div 
          className={`filter-dropdown ${openUpward ? 'upward' : ''}`}
          style={{
            maxHeight: '250px',
            overflowY: 'auto'
          }}
        >
          {filteredAccounts.length === 0 ? (
            <div className="no-options">No accounts found</div>
          ) : (
            filteredAccounts.map((account, index) => (
              <div
                key={account.id}
                ref={el => optionsRef.current[index] = el}
                className={`filter-option ${highlightedIndex === index ? 'highlighted' : ''} ${selectedValues.includes(account.id.toString()) ? 'selected' : ''}`}
                onClick={() => handleToggleAccount(account.id.toString())}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(account.id.toString())}
                  onChange={() => {}} // Handled by parent click
                  className="option-checkbox"
                />
                <span className="option-text">{account.name}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
